import { Network, RotateCcw } from "lucide-react";

import type { CompositionSide, DeploymentComposition, EchelonCitation } from "../../api/types";
import { Callout } from "../../components/Callout";
import { Motion, Presence } from "../../components/presence";
import { Button } from "../../components/ui";
import { isCompany } from "../../company";
import { isNeutral } from "../../enemyDisplay";
import { unitDesignation, unitDesignations } from "../../i18n/designation";
import { t, tServer } from "../../i18n/i18n";
import { plural } from "../../plural";
import { useDeployment } from "../../map/units/useDeployment";
import { useUi } from "../../state/ui";
import { compositionDigest, sideView } from "./rows";
import { useUnitActions } from "./useUnitActions";

/** Composition propre au dossier dans les Données initiales (docs/18 §6.6), sous la force et sous l'ennemi : unités
 *  ajoutées, retirées, rôles choisis ; côté ennemi, ce que chaque paragraphe de l'OPO cite (ENI initial, ultérieur,
 *  futur, appuis, global). Un paragraphe sans unité de la force (repli « éléments de… ») est signalé et l'action
 *  attendue désignée (l'ordre de bataille, pour donner ce rôle à une unité). */

/** Liste des unités citées par un paragraphe, avec sa provenance (« (référence) », « (par défaut) » face à l'ennemi
 *  générique, `neutral`), ou « aucun ». Repli d'un échelon vidé (`elements`) : le serveur ne publie que la formation,
 *  écrite ici dans la langue de l'interface. */
export function citationText(citation: EchelonCitation | undefined, neutral = false): string {
  if (!citation || citation.units.length === 0) return t("aucun");
  const units = citation.units.map(unitDesignation).join(", ");
  if (citation.source === "reference") return neutral ? t("{units} (par défaut)", { units }) : t("{units} (référence)", { units });
  if (citation.source === "elements")
    return t("éléments de la formation {root} (aucune unité de la force)", { root: unitDesignation(citation.units[0] ?? "") });
  return units;
}

/** Résumé de la composition d'un camp, en clair et accordé : compteurs nuls omis (« 1 unité retirée
 *  (113(TI)SONRECO), 1 rôle choisi »). */
export function digestText(digest: { added: string[]; removed: string[]; roles: number }): string {
  const names = (all: string[]) => {
    const list = all.map(unitDesignation);
    return list.length > 4 ? `${list.slice(0, 4).join(", ")}…` : list.join(", ");
  };
  const parts: string[] = [];
  if (digest.added.length)
    parts.push(`${plural(digest.added.length, t("unité ajoutée"), t("unités ajoutées"))} (${names(digest.added)})`);
  if (digest.removed.length)
    parts.push(`${plural(digest.removed.length, t("unité retirée"), t("unités retirées"))} (${names(digest.removed)})`);
  if (digest.roles) parts.push(plural(digest.roles, t("rôle choisi"), t("rôles choisis")));
  return t("Composition propre à ce dossier : {parts}.", { parts: parts.join(", ") || t("aucune modification") });
}

/** Correspondance de l'ennemi avec l'OPO, une ligne par paragraphe (`neutral` : face à l'ennemi générique ;
 *  `company` : ordre d'un S/GTIA, sans paragraphe de reconnaissance ennemie, docs/21 §6). */
export function opoLines(
  composition: DeploymentComposition,
  neutral = false,
  company = false,
): { label: string; text: string; warn: boolean }[] {
  const opo = composition.opo;
  const line = (label: string, citation: EchelonCitation) => ({
    label,
    text: citationText(citation, neutral),
    warn: citation.source === "elements",
  });
  return [
    line(t("ENI initial"), opo.initial),
    // Paragraphe « Renseignement sur l'ennemi » (§1.a) : il cite le trio de reconnaissance de la carte, quel que soit
    // le rôle de chaque unité — d'où son nom ici, et non « Reconnaissance ». L'ordre d'un S/GTIA n'en a pas.
    ...(company ? [] : [line(t("Renseignement sur l'ennemi"), opo.reco)]),
    line(t("ENI ultérieur"), opo.ulterieur),
    {
      label: t("ENI futur"),
      text: opo.futur.units.length
        ? t("renforts {units}", { units: citationText(opo.futur, neutral) })
        : neutral
          ? t("texte par défaut, sans renforts")
          : t("texte de référence, sans renforts"),
      warn: false,
    },
    line(t("Appuis"), opo.appuis),
    { label: t("ENI global"), text: opo.global ? unitDesignation(opo.global) : t("aucun"), warn: false },
  ];
}

export function CompositionSummary({ side }: { side: CompositionSide }) {
  const { data: deployment, isPlaceholderData } = useDeployment();
  const showObjects = useUi((s) => s.showObjects);
  const actions = useUnitActions();
  const composition = isPlaceholderData ? null : (deployment?.composition ?? null);
  const view = composition ? sideView(deployment, side) : null;
  const digest = compositionDigest(view);
  const modified = Boolean(view?.out.modified);
  const lines =
    side === "ENI" && composition
      ? opoLines(composition, isNeutral(deployment?.reference_enemy), isCompany(deployment?.level))
      : [];
  const warned = lines.filter((l) => l.warn);
  const notes = view?.out.notes ?? [];
  const show = Boolean(view) && (modified || lines.length > 0 || notes.length > 0);

  return (
    <Presence show={show}>
      {show && view && (
        <Motion origin="fade" className="space-y-1.5" data-attention={`composition-summary-${side}`}>
          {modified && (
            <Callout tone="info">{digestText(digest)}</Callout>
          )}
          {lines.length > 0 && (
            <div className="rounded-lg bg-canvas px-2.5 py-1.5">
              <p className="pb-0.5 text-meta font-medium text-ink-soft">{t("Ennemi dans l'OPO")}</p>
              <ul className="space-y-0.5">
                {lines.map((l) => (
                  <li key={l.label} className={l.warn ? "text-meta leading-snug text-warn" : "text-meta leading-snug text-ink-muted"}>
                    <span className="font-medium">{t("{label} :", { label: l.label })}</span> {l.text}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {(warned.length > 0 || notes.length > 0) && (
            <Callout tone="warn">
              <ul className="space-y-0.5">
                {warned.map((l) => (
                  <li key={l.label}>
                    {t("{paragraph} : aucune unité de la force ne tient ce rôle ; l'OPO cite des éléments de la force. Donnez ce rôle à une unité depuis l'ordre de bataille.", {
                      paragraph: l.label,
                    })}
                  </li>
                ))}
                {notes.map((note) => (
                  <li key={note}>{unitDesignations(tServer(note))}</li>
                ))}
              </ul>
            </Callout>
          )}
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 pt-0.5">
            <Button variant="link" highlight={warned.length > 0} onClick={() => showObjects(side)}>
              <Network size={14} /> {t("Voir l'ordre de bataille")}
            </Button>
            {modified && !view.legacy && (
              <Button
                variant="link"
                disabled={actions.writes.busy}
                blocked={!actions.writes.canEdit || Boolean(actions.pendingZone)}
                onBlocked={actions.refuse}
                onClick={() => actions.writes.compose.mutate({ op: "reset", side })}
              >
                <RotateCcw size={14} /> {t("Tout rétablir")}
              </Button>
            )}
          </div>
        </Motion>
      )}
    </Presence>
  );
}
